import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  fetchVenueConfig,
  getDefaultVenue
} from "../APIs/Venue/GetVenueConfig";
import { useLoadingStatus } from "../context/LoadingStatusContext";
import { getBookingStages } from "../utils/booking";
import { dmnStorage } from "../utils/dmnStorage";
import { useAppContext } from "./useAppContext";
import { useDiaryContext } from "./useDiaryContext";
import { useGetVenues } from "./useFetchVenues";

const useManageAPIVenueData = () => {
  const { updateStage } = useLoadingStatus();
  const { venueGroupConfig } = useAppContext();
  const { setDiaryContext, selectedVenue, isInitialising } = useDiaryContext();
  const venueGroupId = venueGroupConfig?._id;

  const { allVenues } = useGetVenues(venueGroupId);

  const {
    data: venueConfigData,
    isError: hasVenueConfigError,
    isLoading: isVenueConfigLoading,
    error: venueConfigError
  } = useQuery({
    queryKey: ["venueConfig", selectedVenue],
    queryFn: () => fetchVenueConfig(selectedVenue),
    gcTime: 0,
    enabled: !!selectedVenue,
    refetchInterval: 5 * 60 * 1000, // 5 minutes in milliseconds
    refetchIntervalInBackground: false // Optional: don't refetch when tab is not active
  });

  useEffect(() => {
    if (!selectedVenue) return;

    if (isVenueConfigLoading) {
      updateStage("venueConfig", "loading");
    } else if (hasVenueConfigError) {
      updateStage("venueConfig", "error", {
        message: "Failed to load venue configuration.",
        technicalDetails:
          venueConfigError instanceof Error
            ? venueConfigError.message
            : `fetchVenueConfig() failed for selectedVenue: ${selectedVenue}`
      });
    } else if (venueConfigData) {
      updateStage("venueConfig", "success");
    }
  }, [
    selectedVenue,
    isVenueConfigLoading,
    hasVenueConfigError,
    venueConfigData,
    updateStage
  ]);

  useEffect(() => {
    if (allVenues?.length === 0 && isInitialising) {
      setDiaryContext({
        selectedVenue: undefined,
        venueConfig: {},
        venues: [],
        stages: [],
        isInitialising: false
      });
      return;
    }

    if (selectedVenue || !allVenues || allVenues.length === 0) {
      return;
    }

    (async () => {
      const defaults = await getDefaultVenue({ venues: allVenues });
      setDiaryContext({
        selectedVenue: defaults,
        venues: allVenues
      });
    })();
  }, [selectedVenue, allVenues]);

  useEffect(() => {
    if (!venueConfigData || !venueGroupConfig) {
      return;
    }

    dmnStorage.add("venueId", venueConfigData._id);
    window.locale = `en-${venueConfigData.country_code ?? "GB"}`;
    setDiaryContext({
      venueConfig: venueConfigData,
      venues: allVenues?.filter((venue) => !!venue.title) || [],
      stages: getBookingStages({
        venueConfig: venueConfigData,
        venueGroupConfig
      })
    });
  }, [venueConfigData, venueGroupConfig, allVenues]);
};
export default useManageAPIVenueData;
